import { Component, OnDestroy, inject } from '@angular/core';
import { Settings } from '@app/core/_models/settings';
import { filter, first, publishLast, refCount } from 'rxjs/operators';
import { Observable, Subscription } from 'rxjs';
import { ConsentsTranslations } from '@app/shared/models/consents-translations';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Member } from '@app/core/_models/member';
import { SettingsService } from '@app/core/settings/settings.service';
import { MemberService } from '@app/core/members/member.service';
import { BaseParamsService } from '@app/core/base-params.service';
import { Angulartics2 } from 'angulartics2';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import * as fromRoot from '@app/core/store/reducers';
import { LoadMember } from '@app/core/store/member/member-actions';
import { MsisdnInputComponent } from '@app/shared/components/msisdn-input/msisdn-input.component';
import { PrivacyPolicyComponent } from '@app/shared/components/privacy-policy/privacy-policy.component';
import { TranslatePipe } from '@ngx-translate/core';
import { ConsentsComponent } from '@app/shared/components/consents/consents.component';
import { AsyncPipe, NgClass } from '@angular/common';
import { AppFocusableDirective } from '@app/shared/directives/focusable.directive';
import { InputErrorsComponent } from '@app/shared/components/input-errors/input-errors.component';
import { UPDATE_CONFIRMATION_PATH } from '@app/+profile-form/profile-routes';
import { REDIRECT_QUERY_PARAM } from '@app/core/consts';
import { TranslationsService } from '@app/core/settings/translations.service';

@Component({
  selector: 'app-consent-form',
  templateUrl: './consent-form.component.html',
  imports: [
    MsisdnInputComponent,
    PrivacyPolicyComponent,
    TranslatePipe,
    ConsentsComponent,
    ReactiveFormsModule,
    NgClass,
    AsyncPipe,
    AppFocusableDirective,
    InputErrorsComponent,
  ],
})
export class ConsentFormComponent implements OnDestroy {
  public settings?: Settings;
  public settingsObservable = this.settingsService.getSettingsOnce().pipe(publishLast(), refCount());
  public settingsSub: Subscription;
  public consentsTranslations$: Observable<ConsentsTranslations> = inject(TranslationsService).getConsentTranslations();
  public form: UntypedFormGroup;
  public memberConsents: any;
  public consentKey: string | null;
  public formErrors: any = {};
  public member?: Member;
  public isSubmitted = false;

  constructor(private settingsService: SettingsService,
              private memberService: MemberService,
              private baseParamsService: BaseParamsService,
              formBuilder: UntypedFormBuilder,
              private analytics: Angulartics2,
              private router: Router,
              private route: ActivatedRoute,
              private store: Store<fromRoot.State>) {

    this.form = formBuilder.group({
      consents: {},
      email: ['', [Validators.email, Validators.required]],
      msisdn: '',
    });

    this.route.paramMap.pipe(first()).subscribe((params) => this.onParamsReceived(params));
  }

  private onParamsReceived(params: ParamMap): void {
    this.consentKey = params.get('consentKey');
    this.loadSchemaForForm();
  }

  private loadSchemaForForm(): void {
    this.settingsSub = this.settingsObservable.subscribe((settings) => this.onSettingsFetched(settings));
  }

  private onSettingsFetched(settings: Settings): void {
    this.settings = settings;
    if (!this.consentKey || this.isConsentKeyAvailable(this.consentKey)) {
      this.loadMember();
    } else {
      this.router.navigate([this.baseParamsService.getRootURI(), '404'], { skipLocationChange: true });
    }
  }

  private isConsentKeyAvailable(consentKey: string): boolean {
    return typeof this.settings?.schema.consents?.[consentKey] !== 'undefined';
  }

  private loadMember(): void {
    this.memberService.get()
      .pipe(
        filter((member) => !!member),
        first(),
      ).subscribe((member) => {
      member = this.memberService.processMemberToProfileForm(member);
      this.member = new Member(member);

      if (this.member.consents) {
        this.form.get('consents')?.patchValue(this.member.consents);
        this.memberConsents = this.member.consents;
      }
    });
  }

  public onSave(form: any): void {

    this.isSubmitted = true;
    const errors: Record<string, unknown> = {};

    if (this.form.get('consents')?.invalid) {
      errors.consents = {
        property: 'consents',
        error: 'invalid_consents',
      };
    }

    if (!this.member?.hasProperty('msisdn') && form.value.consents.sms_marketing && this.form.get('msisdn')?.invalid) {
      errors.msisdn = {
        property: 'msisdn',
        error: 'invalid_msisdn',
      };
    }

    if (!this.member?.hasProperty('email') && form.value.consents.email_marketing && this.form.get('email')?.invalid) {
      errors.email = {
        property: 'email',
        error: 'invalid_email',
      };
    }

    this.formErrors = Object.keys(errors).length ? errors : [];

    if (!Object.keys(this.formErrors).length) {

      const consents = JSON.parse(JSON.stringify(form.value.consents));
      Object.keys(consents).forEach(key => {
        consents[key] = { status: consents[key] };
      });

      const body: Record<string, unknown> = { consents: consents };

      if (!this.member?.hasProperty('msisdn') && form.value.consents.sms_marketing) {
        body.msisdn = this.form.value.msisdn;
      }

      if (!this.member?.hasProperty('email') && form.value.consents.email_marketing) {
        body.email = this.form.value.email;
      }

      this.memberService.updateConsents({ member_form: body }).subscribe(
        () => this.onSuccessSave(),
        (response) => this.onErrorSave(response.error),
      );
    }
  }

  private onSuccessSave(): void {
    this.analytics.eventTrack.next({
      action: 'update',
      properties: { category: 'member_change', label: 'Succesfull consents change' },
    });
    this.store.dispatch(new LoadMember(true));


    this.route.queryParamMap.pipe(first()).subscribe((paramMap) => {
      const redirect = paramMap.get(REDIRECT_QUERY_PARAM);
      if (redirect) {
        this.router.navigateByUrl(redirect, { /* Removed unsupported properties by Angular migration: relativeTo. */
          replaceUrl: true,
        });
      } else {
        this.router.navigate(
          [this.consentKey ? '../../' : '../', UPDATE_CONFIRMATION_PATH],
          { relativeTo: this.route, replaceUrl: true },
        );
      }
    });
  }

  private onErrorSave(error: { errors: Record<string, ({ error: string; [properties: string]: any })[]> }): void {
    this.formErrors = Object.entries(error.errors)
      .reduce((acc, [key, value]) => {
        if (Array.isArray(value) && value.length > 0) {
          acc[key] = value[0];
        }

        return acc;
      }, {} as Record<string, { error: string; [properties: string]: any }>);
  }

  public ngOnDestroy(): void {
    this.settingsSub.unsubscribe();
  }
}
